import { type MediaAsset } from '@resolute/schema';
import { cn, Modal } from '@resolute/ui';
import { useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { useState } from 'react';

import { ProgressiveImage } from '@/components/progressive-image';
import { imageUrl } from '@/lib/image';
import { useInfiniteCarousel } from '@/lib/use-infinite-carousel';

/** Fixed height for the main photo — chosen to sit well next to the enquiry
 *  form. Photos fill the box edge-to-edge (`fit="cover"`, the default),
 *  cropping to match its proportions rather than leaving empty space. */
const PHOTO_HEIGHT = 'h-[420px] sm:h-[480px] lg:h-[520px]';

/** Property gallery: an auto-advancing, seamless-loop primary carousel with a
 *  thumbnail strip and a lightbox. Auto-play pauses on hover (and while the
 *  lightbox is open) and is disabled for reduced-motion users. */
export function Gallery({ media, title }: { media: MediaAsset[]; title: string }) {
  const images = media.filter((m) => m.type === 'image');
  const reduce = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const { slides, pos, active, animate, go, goTo, onTransitionEnd } = useInfiniteCarousel(images, {
    auto: !reduce && !lightbox,
    paused,
  });

  if (images.length === 0) {
    return <div className={cn('w-full rounded-lg bg-muted', PHOTO_HEIGHT)} aria-hidden="true" />;
  }

  const current = images[active]!;
  const multiple = images.length > 1;

  return (
    <div className="flex flex-col gap-3">
      <div
        className="group relative w-full overflow-hidden rounded-lg"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {multiple ? (
          <div
            className={cn(
              'flex',
              animate ? 'transition-transform duration-700 ease-out-expo' : 'transition-none',
            )}
            style={{ transform: `translateX(-${pos * 100}%)` }}
            onTransitionEnd={onTransitionEnd}
          >
            {slides.map((img, i) => (
              <div key={`${img.url}-${i}`} className="w-full shrink-0">
                {/* Every slide must load eagerly (the carousel reveals slides
                    via CSS transform, not scroll, so native `loading="lazy"`
                    never gets a chance to re-evaluate off-screen slides — they
                    can end up permanently stuck unloaded), but only the
                    currently-visible one needs high fetch priority. */}
                {/* `useInfiniteCarousel` prepends/appends clones of the last
                    /first item (indices 0 and slides.length - 1) for the
                    seamless loop — those are decorative duplicates and get an
                    empty alt; every real slide gets its own caption. */}
                <ProgressiveImage
                  src={img.url}
                  alt={i === 0 || i === slides.length - 1 ? '' : img.alt || title}
                  blurDataUrl={img.blurDataUrl}
                  aspect=""
                  className={cn('w-full', PHOTO_HEIGHT)}
                  sizes="(min-width: 1024px) 800px, 100vw"
                  priority={i === pos}
                  eager={i !== pos}
                />
              </div>
            ))}
          </div>
        ) : (
          <ProgressiveImage
            src={current.url}
            alt={current.alt || title}
            blurDataUrl={current.blurDataUrl}
            aspect=""
            className={cn('w-full', PHOTO_HEIGHT)}
            sizes="(min-width: 1024px) 800px, 100vw"
            priority
          />
        )}
        <button
          type="button"
          onClick={() => setLightbox(true)}
          aria-label="Open full-screen gallery"
          className="absolute right-3 top-3 z-10 flex size-10 items-center justify-center rounded bg-background/80 text-foreground backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background"
        >
          <Expand className="size-4" aria-hidden="true" />
        </button>
        {multiple && (
          <>
            <GalleryNav dir="prev" onClick={() => go(-1)} />
            <GalleryNav dir="next" onClick={() => go(1)} />
          </>
        )}
      </div>

      {multiple && (
        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Gallery thumbnails">
          {images.map((img, i) => (
            <button
              key={`${img.url}-${i}`}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`View image ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-16 w-24 shrink-0 overflow-hidden rounded border-2 bg-muted transition-colors duration-base ease-out-expo ${
                i === active ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <ProgressiveImage
                src={img.url}
                alt=""
                aspect=""
                widths={[96, 192]}
                sizes="96px"
                className="size-full"
              />
            </button>
          ))}
        </div>
      )}

      <Modal open={lightbox} onOpenChange={setLightbox} title={title} hideTitle className="max-w-4xl">
        <div className="relative">
          <img
            src={imageUrl(current.url, 1600)}
            alt={current.alt || title}
            className="w-full rounded"
            decoding="async"
          />
          {multiple && (
            <>
              <GalleryNav dir="prev" onClick={() => go(-1)} />
              <GalleryNav dir="next" onClick={() => go(1)} />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

function GalleryNav({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'prev' ? 'Previous image' : 'Next image'}
      className={`absolute top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded bg-background/80 text-foreground backdrop-blur transition-colors duration-fast ease-out-expo hover:bg-background ${
        dir === 'prev' ? 'left-3' : 'right-3'
      }`}
    >
      <Icon className="size-5" aria-hidden="true" />
    </button>
  );
}
